import type { Tile } from "@word-duel/shared";
import { GameRuleError } from "./errors";
import type { RandomInt } from "./types";

export interface TileDistributionEntry {
  readonly letter: string;
  readonly count: number;
  readonly value: number;
}

export const BLANK_TILE_LETTER = "?";

export const TILE_DISTRIBUTION: readonly TileDistributionEntry[] = Object.freeze([
  { letter: "A", count: 9, value: 1 },
  { letter: "B", count: 2, value: 3 },
  { letter: "C", count: 2, value: 3 },
  { letter: "D", count: 4, value: 2 },
  { letter: "E", count: 12, value: 1 },
  { letter: "F", count: 2, value: 4 },
  { letter: "G", count: 3, value: 2 },
  { letter: "H", count: 2, value: 4 },
  { letter: "I", count: 9, value: 1 },
  { letter: "J", count: 1, value: 8 },
  { letter: "K", count: 1, value: 5 },
  { letter: "L", count: 4, value: 1 },
  { letter: "M", count: 2, value: 3 },
  { letter: "N", count: 6, value: 1 },
  { letter: "O", count: 8, value: 1 },
  { letter: "P", count: 2, value: 3 },
  { letter: "Q", count: 1, value: 10 },
  { letter: "R", count: 6, value: 1 },
  { letter: "S", count: 4, value: 1 },
  { letter: "T", count: 6, value: 1 },
  { letter: "U", count: 4, value: 1 },
  { letter: "V", count: 2, value: 4 },
  { letter: "W", count: 2, value: 4 },
  { letter: "X", count: 1, value: 8 },
  { letter: "Y", count: 2, value: 4 },
  { letter: "Z", count: 1, value: 10 },
  { letter: BLANK_TILE_LETTER, count: 2, value: 0 }
] satisfies readonly TileDistributionEntry[]);

function tileId(letter: string, ordinal: number): string {
  const label = letter === BLANK_TILE_LETTER ? "BLANK" : letter;
  return `${label}-${String(ordinal).padStart(2, "0")}`;
}

const STANDARD_TILES: readonly Readonly<Tile>[] = Object.freeze(
  TILE_DISTRIBUTION.flatMap(({ letter, count, value }) =>
    Array.from({ length: count }, (_, index) =>
      Object.freeze({ id: tileId(letter, index + 1), letter, value })
    )
  )
);

export const STANDARD_TILE_IDS: readonly string[] = Object.freeze(
  STANDARD_TILES.map(({ id }) => id)
);

export const STANDARD_TILE_CATALOG: ReadonlyMap<string, Readonly<Tile>> = new Map(
  STANDARD_TILES.map((tile) => [tile.id, tile])
);

export function createTileSet(): Tile[] {
  return STANDARD_TILES.map(({ id, letter, value }) => ({ id, letter, value }));
}

export function secureRandomInt(maxExclusive: number): number {
  if (!Number.isSafeInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > 0x1_0000_0000) {
    throw new GameRuleError("INVALID_RANDOM_VALUE", "Random upper bound is invalid.", {
      maxExclusive
    });
  }
  const cryptoObject = globalThis.crypto;
  if (cryptoObject === undefined) {
    throw new Error("A cryptographically secure Web Crypto implementation is required.");
  }

  const range = 0x1_0000_0000;
  const acceptanceLimit = Math.floor(range / maxExclusive) * maxExclusive;
  const values = new Uint32Array(1);
  let candidate = range;
  while (candidate >= acceptanceLimit) {
    cryptoObject.getRandomValues(values);
    candidate = values[0] ?? range;
  }
  return candidate % maxExclusive;
}

/** Fisher-Yates shuffle. Supplying RandomInt makes fixtures exactly reproducible. */
export function shuffleTiles(
  tiles: readonly Tile[],
  randomInt: RandomInt = secureRandomInt
): Tile[] {
  const shuffled = tiles.map((tile) => ({ ...tile }));
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapWith = randomInt(index + 1);
    if (!Number.isInteger(swapWith) || swapWith < 0 || swapWith > index) {
      throw new GameRuleError(
        "INVALID_RANDOM_VALUE",
        "Random source returned an out-of-range value.",
        {
          maxExclusive: index + 1,
          received: swapWith
        }
      );
    }
    const current = shuffled[index];
    const other = shuffled[swapWith];
    if (current === undefined || other === undefined) {
      throw new Error("Unreachable shuffle index.");
    }
    shuffled[index] = other;
    shuffled[swapWith] = current;
  }
  return shuffled;
}

export function tileRackValue(tiles: readonly Tile[]): number {
  return tiles.reduce((total, tile) => total + tile.value, 0);
}

export function tileLetter(tile: Tile): string {
  return tile.letter === BLANK_TILE_LETTER
    ? (tile.assignedLetter ?? BLANK_TILE_LETTER)
    : tile.letter;
}
